import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { ArrowLeft } from '@styled-icons/feather';
import { useTheme } from 'next-themes';
import { Location, Path } from 'slate';
import { insertNodes, removeNodes } from '@udecode/plate-core';
import mixpanel from 'mixpanel-browser';
import { ITemplate, TEMPLATE_CATEGORY, TEMPLATE_TYPE } from '@typedream/data';
import { useGetTemplateQuery, useListSectionTemplatesQuery } from '@/store';
import { DreamElementOrText } from '@/sharedComponents/TextEditor/plate';
import { DEFAULT_BIG_SIDEBAR_WIDTH, SIDEBAR_ZINDEX_OPEN } from '@/sharedComponents/TextEditor/constants';
import SidebarItem from '@/sharedComponents/TextEditor/components/SidebarItem';
import TemplateItem from '@/sharedComponents/TextEditor/components/SectionTemplateSidebar/TemplateItem';
import { DARK_THEME } from '@/constants';
import { useDreamEditorRef } from '@typedream/dream-editor';
import { useSpaceIDFromPath } from '@/data/space/hooks';
import styled from '@emotion/styled';
import { EVENT } from '@/analytics';
import Overlay from '../Overlay';

function fmtCategory(category: string): string {
  const cat = category.replaceAll('_', ' ');
  return cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase();
}

const StyledSidebar = styled.div`
  position: fixed;
  left: 0px;
  top: 0px;
  height: 100vh;
  background-color: var(--side-background);
  border-right: 1px solid var(--border-modal);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  transition: all 0.3s;
  box-shadow: var(--shadow-modal);
  padding: 10px;
`;

const StyledBlock = styled.div`
  padding: 20px 0px;
`;

const StyledBlockOptions = styled.div`
  padding: 20px 0px;
  width: 140px;
`;

const StyledSidebarMiddle = styled.div`
  flex-grow: 1;
  min-height: 1px;
  align-items: center;
  justify-content: flex-start;
  overflow-x: auto;
  padding-bottom: 20px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
`;

const StyledBlockTemplate = styled.div`
  flex: 1;
  height: 100%;
  overflow-y: auto;
  margin-left: 5px;
`;

function SectionTemplateSidebar() {
  const editor = useDreamEditorRef();
  const spaceID = useSpaceIDFromPath();

  const [category, setcategory] = useState(TEMPLATE_CATEGORY.HERO);
  const { theme } = useTheme();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [templatePath, setTemplatePath] = useState<Location | null>(null);
  const [hovered, setHovered] = useState<ITemplate<TEMPLATE_TYPE.SECTION> | null>(null);

  useEffect(() => {
    function handler(loc: Location) {
      setTemplatePath(loc);
      setSidebarOpen((v) => !v);
    }
    editor.emitter.on('sidebar:toggle-section-template', handler);
    return () => {
      editor.emitter.off('sidebar:toggle-section-template', handler);
    };
  }, [editor]);

  const { data: SECTION_TEMPLATES } = useListSectionTemplatesQuery();

  const closeSidebar = () => {
    setSidebarOpen(false);
    setTemplatePath(null);
  };

  const handleCancel = () => {
    removeNodes(editor, { at: templatePath });
    closeSidebar();
  };

  useEffect(() => {
    if (!hovered || !sidebarOpen) return;

    // calculate the path to delete the previous template
    const deletePath = Path.next(templatePath as Path);

    let node: DreamElementOrText = hovered.published_content || { type: 'p', children: [{ text: '' }] };

    // default templates hardcoded in /packages/data/src/store/templates/defaults
    // will already have the content available, so no need to fetch.
    // This is for templates from backend database
    if (!hovered.published_content && hovered.id) {
      useGetTemplateQuery.run([spaceID, hovered.id]).then(({ data }) => {
        if (data && data.published_content) {
          node = data.published_content as DreamElementOrText;
        }
        insertNodes(
          editor,
          node,
          { at: templatePath },
        );
        removeNodes(editor, { at: deletePath });
      });
    } else {
      insertNodes(
        editor,
        node,
        { at: templatePath },
      );
      removeNodes(editor, { at: deletePath });
    }
  }, [hovered, spaceID, templatePath, editor, sidebarOpen]);

  useEffect(() => {
    setHovered(null);
  }, [sidebarOpen]);

  if (!SECTION_TEMPLATES) {
    return null;
  }

  return (
    <>
      {sidebarOpen && <Overlay onClick={handleCancel} />}
      <StyledSidebar
        data-testid="SectionTemplateSidebar_container"
        style={{
          width: DEFAULT_BIG_SIDEBAR_WIDTH,
          left: sidebarOpen ? 0 : -DEFAULT_BIG_SIDEBAR_WIDTH - 10,
          zIndex: SIDEBAR_ZINDEX_OPEN + 1, // the overlay has z-index of 2
        }}
      >
        <StyledBlock>
          <div
            role="none"
            className={classNames(
              'with-cursor-pointer is-flex is-flex-direction-row is-justify-content-flex-start is-align-items-center is-size-6 with-light-text-color',
            )}
            onClick={handleCancel}
          >
            <ArrowLeft
              size={18}
              style={{ marginRight: 10, cursor: 'pointer' }}
            />
            Section Templates
          </div>
        </StyledBlock>
        <StyledSidebarMiddle>
          <StyledBlockOptions>
            {
              Object.entries(SECTION_TEMPLATES).map(([k, v]) => (
                <SidebarItem
                  key={k}
                  selected={category === k}
                  center={fmtCategory(v.title)}
                  className="mt-1 mb-1 is-subtitle-size with-text-color"
                  onClick={() => setcategory(k as TEMPLATE_CATEGORY)}
                />
              ))
            }
          </StyledBlockOptions>
          <StyledBlockTemplate>
            {
              Object.entries(SECTION_TEMPLATES[category]?.templates).map(([k, v]) => (
                <TemplateItem
                  key={k}
                  img={(theme === DARK_THEME && v.metadata.image_sidebar_dark)
                    || (theme === DARK_THEME && v.metadata.image_dark)
                    || v.metadata.image_sidebar
                    || v.metadata.image}
                  active={(hovered?.id && hovered?.id === v.id) || hovered === v}
                  onHover={() => setHovered(v)}
                  onMouseLeave={() => setHovered(null)}
                  onSelectTemplate={() => {
                    mixpanel.track(EVENT.CLICK_SECTION_TEMPLATE, {
                      section_template_name: k,
                    });
                    setSidebarOpen(false);
                  }}
                />
              ))
            }
          </StyledBlockTemplate>
        </StyledSidebarMiddle>
      </StyledSidebar>
    </>
  );
}

export default SectionTemplateSidebar;
